/*----------------------------------
- HOOK: PUBLIC ROW PREVIEW DRAWER
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @adr  ADR-0028
 * @rule A preview opens no authentication. Only the drawer's CTA hands off, through the page's
 *       own conversion path, after closing the drawer (`onRequestClose('cta')`) so the auth modal
 *       it opens is never covered. The focus goes back to the row through the opener hook every
 *       public preview drawer shares (`usePreviewDrawerOpener`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import usePreviewDrawerOpener from '@/client/pages/landing/components/previewDrawer/usePreviewDrawerOpener';

// Types
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import type { PublicDomainsTableRow } from '../components/table/PublicDomainsTable';
import type { PreviewDrawerCloseReason } from '@/client/pages/landing/components/previewDrawer/useLazyPreviewDrawer';

/*----------------------------------
- TYPES
----------------------------------*/

type PublicRowPreviewDrawerOptions = {
    /** Analytics page id, `domains_public` or `landing_home`. */
    page: string;
    /** Analytics section, and the drawer events' `surface`. */
    section: 'domains_results' | 'landing_domains_preview';
    triggerId: Extract<ConversionTriggerId, 'website_domains_row_preview' | 'website_landing_row_preview'>;
    /** The rows on screen, in their order: what the drawer's up and down arrows walk. */
    rows: readonly PublicDomainsTableRow[];
};

type PublicRowPreviewDrawer = {
    previewRow: PublicDomainsTableRow | null;
    openPreview: (row: PublicDomainsTableRow) => void;
    closePreview: (reason: PreviewDrawerCloseReason) => void;
    /** Absent on the first row, like the Product drawer's disabled arrow. */
    showPrevious?: () => void;
    /** Absent on the last row. */
    showNext?: () => void;
};

/*----------------------------------
- HOOK
----------------------------------*/

const usePublicRowPreviewDrawer = ({
    page,
    section,
    triggerId,
    rows,
}: PublicRowPreviewDrawerOptions): PublicRowPreviewDrawer => {
    const [previewRow, setPreviewRow] = React.useState<PublicDomainsTableRow | null>(null);
    const { rememberOpener, returnFocus } = usePreviewDrawerOpener();

    const openPreview = React.useCallback(
        (row: PublicDomainsTableRow) => {
            const domain = row.row.domain;
            rememberOpener();
            setPreviewRow(row);
            trackConversionTrigger(triggerId, { section, context: { domain } });
            // `via` splits row clicks from arrow walking, so walking never counts as an open.
            track('domain_preview_drawer_shown', { page, domain, surface: section, via: 'click' });
        },
        [page, rememberOpener, section, triggerId],
    );

    const closePreview = React.useCallback(
        (reason: PreviewDrawerCloseReason) => {
            if (previewRow) {
                track('domain_preview_drawer_dismissed', { page, domain: previewRow.row.domain, surface: section, reason });
            }
            setPreviewRow(null);
            returnFocus(reason);
        },
        [page, previewRow, returnFocus, section],
    );

    /*
     * Walking to a neighbour is not a row click: it records the new row as shown, but fires no
     * preview trigger and keeps the opener, so closing still returns focus to the row the visitor
     * clicked.
     */
    const currentIndex = previewRow ? rows.findIndex((entry) => entry.key === previewRow.key) : -1;
    const showRowAt = React.useCallback(
        (index: number) => {
            const next = rows[index];
            if (!next) return;
            setPreviewRow(next);
            track('domain_preview_drawer_shown', { page, domain: next.row.domain, surface: section, via: 'arrow' });
        },
        [page, rows, section],
    );
    const showPrevious = currentIndex > 0 ? () => showRowAt(currentIndex - 1) : undefined;
    const showNext = currentIndex >= 0 && currentIndex < rows.length - 1 ? () => showRowAt(currentIndex + 1) : undefined;

    return { previewRow, openPreview, closePreview, showPrevious, showNext };
};

export default usePublicRowPreviewDrawer;
